/** Pilot badges: earned from a pilot's numbers, never stored (so changing a threshold re-awards everybody at once). */
export interface PilotProfile {
  name: string;
  runs: number; // verified runs (races, trials, daily) submitted
  online: number; // online races finished (rematches not counted)
  wins: number; // online races won against at least one other player
  podiums: number; // online podiums in rooms of three or more
  dailyPlayed: number; // days of the daily challenge played
  dailyWins: number; // days won
  bestStreak: number; // longest run of consecutive daily-challenge days
  records: number; // circuit records currently held
  stageRuns: number; // verified runs on a rally stage
  stages: number; // different stages finished
  stagesTotal: number; // stages the game has
  stageClean: number; // stages finished (best run) without touching a wall
  stageRecords: number; // stage records currently held
  tourneys: number; // tournaments played (at least one round)
  finals: number; // finals the pilot qualified for
  qualTop5: number; // closed qualifiers finished in the top 5
  finalTop3: number; // finished tournament finals ended on the podium
}
export interface Badge {
  id: string;
  icon: string; // emoji, for Discord
  en: string; // short, upper case: shown in the game
  fr: string;
  need: (p: PilotProfile) => boolean;
}
export const BADGES: Badge[] = [
  { id: "first", icon: "🏁", en: "FIRST LAP", fr: "Premier tour", need: (p) => p.runs >= 1 },
  { id: "regular", icon: "🔁", en: "REGULAR", fr: "Habitué", need: (p) => p.runs >= 25 },
  { id: "veteran", icon: "🎖️", en: "VETERAN", fr: "Vétéran", need: (p) => p.runs >= 100 },
  { id: "dailyfan", icon: "📅", en: "DAILY FAN", fr: "Fan du défi", need: (p) => p.dailyPlayed >= 5 },
  { id: "dailyaddict", icon: "🗓️", en: "DAILY ADDICT", fr: "Accro au défi", need: (p) => p.dailyPlayed >= 20 },
  { id: "dailychamp", icon: "🏆", en: "DAILY CHAMP", fr: "Champion du jour", need: (p) => p.dailyWins >= 1 },
  { id: "fire", icon: "🔥", en: "ON FIRE", fr: "En feu (7 jours d'affilée)", need: (p) => p.bestStreak >= 7 },
  { id: "online", icon: "🌐", en: "ONLINE RACER", fr: "Pilote en ligne", need: (p) => p.online >= 5 },
  { id: "winner", icon: "🥇", en: "ONLINE WINNER", fr: "Vainqueur en ligne", need: (p) => p.wins >= 1 },
  { id: "podium5", icon: "🥉", en: "PODIUM X5", fr: "5 podiums en ligne", need: (p) => p.podiums >= 5 },
  { id: "record", icon: "⏱️", en: "RECORD HOLDER", fr: "Détenteur d'un record", need: (p) => p.records >= 1 },
  { id: "stage1", icon: "🏔️", en: "FIRST STAGE", fr: "Première étape", need: (p) => p.stages >= 1 },
  { id: "stageregular", icon: "🛞", en: "RALLY REGULAR", fr: "Habitué du rallye (10 courses d'étape)", need: (p) => p.stageRuns >= 10 },
  { id: "stageclean", icon: "✨", en: "CLEAN STAGE", fr: "Étape sans toucher un mur", need: (p) => p.stageClean >= 1 },
  { id: "stagerecord", icon: "🏆", en: "STAGE RECORD", fr: "Détenteur d'un record d'étape", need: (p) => p.stageRecords >= 1 },
  { id: "stageall", icon: "🗺️", en: "ALL STAGES", fr: "Toutes les étapes terminées (3 au moins)", need: (p) => p.stagesTotal >= 3 && p.stages >= p.stagesTotal },
  { id: "tourney1", icon: "🎪", en: "TOURNAMENT", fr: "Participant à un tournoi", need: (p) => p.tourneys >= 1 },
  { id: "finalist", icon: "🎟️", en: "FINALIST", fr: "Qualifié pour une finale", need: (p) => p.finals >= 1 },
  { id: "qualtop5", icon: "⭐", en: "QUAL TOP 5", fr: "Top 5 d'une qualification", need: (p) => p.qualTop5 >= 1 },
  { id: "finalpodium", icon: "🏅", en: "FINAL PODIUM", fr: "Top 3 d'une finale", need: (p) => p.finalTop3 >= 1 },
];
export const earnedBadges = (p: PilotProfile) => BADGES.filter((b) => b.need(p));
